import React, { useState, useEffect } from 'react';
import { api } from '../api';
import './DashboardPages.css';

const EMPTY_FARMER = { name: '', phone: '', location: '' };

function FarmersPage() {
  const [farmers, setFarmers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [createForm, setCreateForm] = useState(EMPTY_FARMER);
  const [creating, setCreating] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FARMER);
  const [savingEdit, setSavingEdit] = useState(false);

  const loadFarmers = async () => {
    try {
      setLoading(true);
      setError(null);
      const { farmers: data } = await api.getFarmers();
      setFarmers(data);
    } catch (err) {
      setError(err.message);
      setFarmers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFarmers();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      setCreating(true);
      setError(null);
      setSuccess(null);
      const name = createForm.name.trim();
      await api.createFarmer(
        name,
        createForm.phone.trim(),
        createForm.location.trim() || undefined
      );
      setCreateForm(EMPTY_FARMER);
      setSuccess(`Farmer "${name}" added successfully.`);
      await loadFarmers();
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (farmer) => {
    setEditModal(farmer);
    setEditForm({
      name: farmer.name,
      phone: farmer.phone,
      location: farmer.location || '',
    });
  };

  const closeEdit = () => {
    if (!savingEdit) setEditModal(null);
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    if (!editModal) return;
    try {
      setSavingEdit(true);
      setError(null);
      setSuccess(null);
      await api.updateFarmer(editModal.farmer_id, {
        name: editForm.name.trim(),
        phone: editForm.phone.trim(),
        location: editForm.location.trim(),
      });
      setEditModal(null);
      setSuccess('Farmer updated.');
      await loadFarmers();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (farmer) => {
    if (!window.confirm(`Delete farmer "${farmer.name}"? This cannot be undone.`)) return;
    try {
      setError(null);
      setSuccess(null);
      await api.deleteFarmer(farmer.farmer_id);
      setEditModal(null);
      setSuccess(`Farmer "${farmer.name}" removed.`);
      await loadFarmers();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="dashboard-page">
      <h1>Farmers</h1>
      <p className="dashboard-subtitle">
        Register and manage farmers manually (in addition to WhatsApp self-registration via orders).
      </p>

      <section className="entity-manual-panel" aria-labelledby="farmer-manual-heading">
        <h2 id="farmer-manual-heading" className="entity-manual-title">
          Manual farmer registration
        </h2>
        <p className="orders-hint entity-manual-hint">
          Required for Milestone 1: administrators can add, correct, or remove farmer records here.
        </p>
        <form className="entity-manual-form" onSubmit={handleCreate}>
          <div className="entity-manual-fields">
            <div className="form-group">
              <label htmlFor="create-farmer-name">Full name *</label>
              <input
                id="create-farmer-name"
                type="text"
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                required
                placeholder="e.g. Jean Mbouda"
              />
            </div>
            <div className="form-group">
              <label htmlFor="create-farmer-phone">WhatsApp / phone *</label>
              <input
                id="create-farmer-phone"
                type="tel"
                value={createForm.phone}
                onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
                required
                placeholder="+237 6XX XXX XXX"
              />
            </div>
            <div className="form-group">
              <label htmlFor="create-farmer-location">Farm / village (optional)</label>
              <input
                id="create-farmer-location"
                type="text"
                value={createForm.location}
                onChange={(e) => setCreateForm({ ...createForm, location: e.target.value })}
                placeholder="e.g. Mbouda"
              />
            </div>
          </div>
          <button type="submit" className="entity-add-btn" disabled={creating}>
            {creating ? 'Saving…' : 'Save new farmer'}
          </button>
        </form>
      </section>

      {success && <div className="orders-success">{success}</div>}
      {error && <div className="orders-error">{error}</div>}

      <h2 className="entity-list-heading">Registered farmers</h2>

      {loading ? (
        <div className="orders-placeholder">
          <p>Loading farmers…</p>
        </div>
      ) : farmers.length === 0 ? (
        <div className="orders-placeholder">
          <p>No farmers in the list yet.</p>
          <p className="orders-hint">
            Use the form above to add one, or wait until someone completes an order on WhatsApp.
          </p>
        </div>
      ) : (
        <div className="orders-list">
          {farmers.map((f) => (
            <div key={f.farmer_id} className="order-card">
              <div className="order-card-header">
                <span className="order-id">#{f.farmer_id}</span>
              </div>
              <div className="order-card-body">
                <p><strong>Name:</strong> {f.name}</p>
                <p><strong>Phone:</strong> {f.phone}</p>
                <p><strong>Location:</strong> {f.location || '—'}</p>
              </div>
              <div className="entity-actions">
                <button type="button" className="entity-btn entity-edit" onClick={() => openEdit(f)}>
                  Edit
                </button>
                <button type="button" className="entity-btn entity-delete" onClick={() => handleDelete(f)}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editModal && (
        <div className="modal-overlay" onClick={closeEdit}>
          <div className="modal-content modal-form" onClick={(e) => e.stopPropagation()}>
            <h3>Edit farmer #{editModal.farmer_id}</h3>
            <form onSubmit={handleEditSave}>
              <div className="form-group">
                <label htmlFor="edit-farmer-name">Name</label>
                <input
                  id="edit-farmer-name"
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-farmer-phone">Phone</label>
                <input
                  id="edit-farmer-phone"
                  type="tel"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-farmer-location">Location</label>
                <input
                  id="edit-farmer-location"
                  type="text"
                  value={editForm.location}
                  onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="modal-close-btn" onClick={closeEdit} disabled={savingEdit}>
                  Cancel
                </button>
                <button type="submit" className="entity-btn entity-edit" disabled={savingEdit}>
                  {savingEdit ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default FarmersPage;
